import { Component, EventEmitter, Input, Optional, Output } from '@angular/core';
import { ValidationErrorComponent } from "@shared/controls/validation-error/validation-error.component";
import { ControlValueAccessor, FormControl, NgControl, ReactiveFormsModule } from "@angular/forms";
import { SelectOption } from "@models/ui-models/select-options";
import { CheckboxComponent } from "../checkbox/checkbox.component";

export interface ICheckboxGroupOption extends SelectOption {
  checked: boolean;
}

@Component({
  selector: 'app-checkbox-group',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CheckboxComponent,
    ValidationErrorComponent,
  ],
  templateUrl: 'checkbox-group.component.html',
  styles: `.header {
    margin-bottom: 4px;
  }`
})
export class CheckboxGroupComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() helperText = '';
  @Input() customErrors: Record<string, string> = { requiredAtLeastOne: 'At least one officer position type must be selected' };
  @Input() disabled = false;

  @Input({ required: true }) set options(options: SelectOption[]) {
    this.controlOptions = options.map((option) => ({ ...option, checked: this.value.includes(option.value) }));

    if (this._originValue?.length) {
      this.writeValue(this._originValue);
    }
  }

  @Output() changed = new EventEmitter<unknown[]>();

  #value: unknown[] = []; // array of checked ICheckboxGroupOption.value
  controlOptions: ICheckboxGroupOption[] = [];
  _originValue: unknown[] | null = null;

  constructor(@Optional() protected ngControl: NgControl) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
  }

  get displayValidationError(): boolean {
    return !!(this.ngControl && this.ngControl.touched && this.ngControl.errors);
  }

  get value(): unknown[] {
    return this.#value;
  }

  set value(value: unknown[]) {
    this.#value = value;
    this.onChange(this.#value);
    this.changed.emit(value);
  }

  onChange: any = (): void => {
  };
  onTouched: any = (): void => {
  };

  onChecked(checked: boolean, checkboxIndex: number): void {
    this.controlOptions.find((_, index) => index == checkboxIndex)!.checked = checked;

    this.value = this.controlOptions
      .filter((option) => option.checked)
      .map((option) => option.value);

    this.onTouched();
  }

  writeValue(value: unknown[]): void {
    this._originValue = value;

    this.controlOptions.forEach((option, index) => {
      this.controlOptions[index].checked = value.includes(option.value);
    });

    const checkedOptions = this.controlOptions
      .filter((option) => option.checked)
      .map((option) => option.value);

    this.value = [...new Set(checkedOptions)]; // Remove Duplicates;
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  static addRequiredAtLeastOneValidator<T>(control: FormControl<T[]> | FormControl<T[] | null>): void {
    control.addValidators((control) => {
      const controlsValue = (control.value ?? []) as boolean[];

      return controlsValue.length
        ? null
        : { requiredAtLeastOne: true };
    });
  }
}
